import { ICustomerCaseData } from 'types/customer-case';

import { colors } from 'styles';
import Typography from '@mui/material/Typography/Typography';

import WhiteSensorIcon from 'assets/white-sensor.svg';
import WhiteAutomationIcon from 'assets/white-automation.svg';
import HeroImage from 'assets/applications/third/hero.webp';

import BluePlaneImage from 'assets/customer-cases/blue-plane.webp';

import PlanesInAirportImage from 'assets/common/images/aeronautics-main.webp';
import DozenOfPlanesImage from 'assets/common/images/aeronautics-topic-2.webp';
import ThreePlanesImage from 'assets/customer-cases/airbus-up-next/three-planes.webp';
import PlaneImage from 'assets/customer-cases/airbus-up-next/plane.webp';

import WhiteAirportIcon from 'assets/industries/airport.svg';
import WhiteAgileIcon from 'assets/white-agile.svg';
import StretchToolIcon from 'assets/customer-cases/stretch-tool.svg';
import WhiteRunwayIcon from 'assets/industries/runway.svg';

import WhiteSimulationIcon from 'assets/customer-cases/simulation.svg';
import WhiteCircleAroundIcon from 'assets/white-circle-around.svg';
import WhiteDirectionRightIcon from 'assets/industries/direction-right.svg';
import { automaticDatasetEnhancementApplicationMeta } from 'constants/pages';

export const forthCustomerCaseData: ICustomerCaseData = {
  heroData: {
    heroTitle: 'Revolutionizing Automated Taxiing in Aviation',
    heroDescription: `Learn how a global aircraft OEM is leveraging AILiveSim to train, test, and validate an intelligent automated taxiing system.
By simulating complex airport conditions with high fidelity,
the team is accelerating innovation, increased safety, and
laid the foundation for scalable automated mobility across hundreds of airports.`,
    heroListTitle: 'Transforming Ground Operations with Intelligent Simulation',
    buttonLabel: 'Explore Aeronautics Industry ',
    buttonLink: '/Industries/aeronautics',
    heroListItems: [
      {
        description:
          'AILiveSim enables synchronized simulation of radar, LiDAR, and over 10 camera feeds to support robust perception systems.',
        Icon: WhiteSensorIcon,
        title: 'Multi-Sensor Intelligence for Safer Navigation',
      },
      {
        description:
          'AILiveSim is working closely with the customer to advance and validate automated taxiing through real-time flight simulation.',
        Icon: WhiteAutomationIcon,
        title: 'Seamless Closed-Loop Integration',
      },
      {
        description:
          'Realistic airport replicas are continuously refined to reflect variable terrain, lighting, weather, and surface conditions for advanced automated testing.',
        Icon: WhiteAirportIcon,
        title: 'Simulation at Scale with Digital Airport Twins',
      },
    ],
    heroImages: {
      firstImage: {
        imageSrc: PlanesInAirportImage,
        altText: 'Planes in airport',
      },
      fourthImage: {
        imageSrc: DozenOfPlanesImage,
        altText: 'Dozen of planes in airport',
      },
      secondImage: {
        imageSrc: ThreePlanesImage,
        altText: 'Three planes in airport',
      },
      thirdImage: {
        imageSrc: PlaneImage,
        altText: 'Single plane in airport',
      },
    },
  },
  customerCase: {
    title: 'The Challenge: Building Confidence in Real-World Airport Conditions',
    description: (
      <Typography fontSize="18px" color={colors.mainDark}>
        To validate automated taxiing in complex, real-world environments, the customer needed a
        simulation platform that could go beyond basic testing.
      </Typography>
    ),
    bottomText:
      'Their existing simulation toolset offered a good foundation—but wasn’t built for the scale and fidelity required to accelerate testing.',
    image: BluePlaneImage,
    items: [
      'Simulate a wide range of weather conditions',
      'Reproduce variable lighting throughout the day',
      'Reflect tarmac wear, pollution, and surface deformation',
      'Model variations in paint visibility and contrast',
      'Include diverse types of airport vehicles and their dynamic movements',
    ],
    link: '',
  },
  firstInformationBlockData: {
    title: 'The Solution: Real-World Accuracy for Next-Gen Automated Taxiing',
    description: `AILiveSim delivers a next-generation simulation environment tailored for intelligent mobility in aviation.
    With multi-sensor data, closed-loop control, and lifelike airport environments, the OEM’s teams are iterating faster, testing safer,
    and validating each new feature with greater confidence.`,
    items: [
      {
        Icon: WhiteAgileIcon,
        description: `Open loop: Train and test perception software in highly variable conditions \n
        Closed loop: Real-time integration with a plane simulator to recreate dynamic aircraft behavior`,
        title: 'Open & Closed Loop Workflows',
      },
      {
        Icon: WhiteSensorIcon,
        description:
          'Camera, radar, and LiDAR — with support for over 10 synchronized camera feeds and high-resolution 3D radars',
        title: 'Multi-Sensor \nSimulation',
      },
      {
        Icon: StretchToolIcon,
        description:
          'AILiveSim provides a high-fidelity, flexible simulation environment to model hundreds of airports and real-world conditions, supporting the customer’s ongoing automated taxiing development.',
        title: 'Scalable Simulation Infrastructure',
      },
      {
        Icon: WhiteRunwayIcon,
        description:
          'Realistic replicas of real-world airports, continuously updated to support variability in terrain, lighting, weather, and traffic for accurate and scalable testing.',
        title: 'Digital Airport\n Twins',
      },
    ],
  },
  resultsData: {
    title: 'Results: Safer Development at Scale with Realistic Airport Simulation',
    description: `
    AILiveSim’s simulation platform enabled the aviation OEM to accelerate automated taxiing development without compromising safety or fidelity. High-resolution, sensor-driven simulation environments helped the team avoid costly and time-consuming field testing, resulting in faster iteration cycles and stronger confidence in real-world readiness.
    `,
    listTitle: 'Key Outcomes',
    // clientFeedbackMessage: `“From multi-sensor simulation to Digital Airport Twins, AILiveSim brings the real world into the loop.”`,
    // clientLabel: '— Lucas M., Project Lead, Innovation Division of a major aircraft OEM',
    listItems: [
      <li key="1" style={{ lineHeight: '140%', fontSize: '16px' }}>
        Accelerated innovation cycles with higher simulation fidelity
      </li>,
      <li key="2" style={{ lineHeight: '140%', fontSize: '16px' }}>
        Seamless integration into development pipelines
      </li>,
      <li key="3" style={{ lineHeight: '140%', fontSize: '16px' }}>
        Risk-free validation of advanced perception systems
      </li>,
      <li key="4" style={{ lineHeight: '140%', fontSize: '16px' }}>
        Enhanced accuracy in complex airport environments
      </li>,
    ],
    resultTitle: 'Cut Field Testing Costs with Simulation-First Development',
    resultDescription:
      'Simulation eliminated the need for constant real-world validation, reducing development time and operational expenses.',
  },
  secondInformationBlockData: {
    title: 'Unlocking the Future of Automated Aviation with Simulation',
    description: `The innovation team now plans to scale AILiveSim’s intelligent simulation to hundreds of airports while automating testing workflows and expanding automation across aviation use cases.`,
    items: [
      {
        Icon: WhiteCircleAroundIcon,
        description: 'Plans to fully automate simulation workflows for broader efficiency.',
        title: 'Automated Testing Pipelines',
      },
      {
        Icon: StretchToolIcon,
        description: 'Vision to scale simulation across hundreds of airports worldwide.',
        title: 'Global Simulation at\nScale',
      },
      {
        Icon: WhiteDirectionRightIcon,
        description: 'Broaden the scope of intelligent simulation beyond taxiing.',
        title: 'Expanded Automation Use Cases',
      },
      {
        Icon: WhiteSimulationIcon,
        description:
          'From sensor fidelity to digital twins, AILiveSim ensures every scenario mimics the real world with precision.',
        title: 'Real-World Accuracy in Every Run',
      },
    ],
  },
  applicationData: {
    bottomText: '',
    description:
      'Collecting and labeling real-world data is time-consuming, costly, and rarely captures the edge cases your AI needs for complex environments. AILiveSim strengthens all existing datasets with intelligent, AI-driven simulation — generating high-quality synthetic data that fills critical gaps, expands coverage, and improves model performance, while saving both time and cost.',
    image: HeroImage,
    items: [],
    title: 'Learn More About Automatic & Intelligent Dataset Enhancement with Simulation',
    link: automaticDatasetEnhancementApplicationMeta.url,
  },
};
